import { TimeRangeInput, computesCrossesMidnight, type TimeRangeInputValue } from './TimeRangeInput';
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from './ui/select';
import { buildLocalIso } from '@/lib/shift-format';
import { addDays } from '@/lib/calendar-day';
import type { ReasonCode } from './MonthlyEntryDaySheet';

/**
 * v0.4後半 月次実績一括入力 第2フェーズ 追加セッション (§7-3)「修正して承認」「承認後修正」共通の
 * 1件分の修正フォーム。設計: docs/design/v04-monthly-attendance-bulk-entry-phase2-detail.md §7-3。
 *
 * ⚠️ 意図的に絞った範囲(次回セッションで要否を相談):
 * 1. 休憩の内訳(breaks)は編集しない。amendments.breaks を送らなければ、サーバー側
 *    (computeAmendedFields、routes/work-records.ts)が既存の休憩内訳を新しい時刻区間で
 *    自動的に再検証してくれる (`breaksInput === undefined` 分岐) ため、時刻だけの修正で
 *    休憩を個別入力する必要が無い。
 * 2. 区分・遅刻早退理由の編集を追加した(2026-09-27追加セッション)が、休暇区分への変更は
 *    サーバー側(D-1038)がブロックする。ブロック時のメッセージはdry-run結果をそのまま表示する
 *    (呼び出し側、WorkRecordApprovalsPage.tsx の BlockedReasonMessage 参照)ため、このフォーム
 *    自身は選択肢を絞り込む・警告を出す等の追加ロジックを持たない(選べても保存できないことを
 *    dry-run/実submit時のエラーで初めて知る設計、既存のD-1038エラーメッセージが十分に明確なため)。
 * MonthlyEntryDaySheet を流用しなかったのは、そちらが「本人の入力」(DayEntry/shifts の複数勤務
 * 抽象化)前提のデータ形をしており、「既存1件を修正」という本フォームの用途に逆変換するより、
 * 軽量フォームを新設する方が単純だったため。
 */
export type WorkRecordAmendDraft = {
  time: TimeRangeInputValue;
  /** 区分(reasonCodeId、null=通常勤務)。amendments には常に現在の選択値を送る(未変更でも
   *  no-opとして安全、既存値と同じならサーバー側の検証もスキップされる)。 */
  reasonCodeId: string | null;
  deviationReasonCodeId: string | null;
};

export const emptyAmendDraft = (
  startAt: string, endAt: string, reasonCodeId: string | null, deviationReasonCodeId: string | null,
): WorkRecordAmendDraft => ({
  time: { startTime: isoToLocalHmSafe(startAt), endTime: isoToLocalHmSafe(endAt) },
  reasonCodeId, deviationReasonCodeId,
});

// isoToLocalHm (lib/shift-format.ts) と同じ変換だが、ここでは import を増やさず素朴に実装する。
function isoToLocalHmSafe(iso: string): string {
  const d = new Date(iso);
  return `${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`;
}

const WORKED_OPTION = '__worked__';
const NONE_OPTION = '__none__';

type Props = {
  value: WorkRecordAmendDraft;
  onChange: (next: WorkRecordAmendDraft) => void;
  reasonCodes: ReasonCode[];
  deviationReasonCodes: ReasonCode[];
};

export function WorkRecordAmendForm({ value, onChange, reasonCodes, deviationReasonCodes }: Props) {
  return (
    <div className="space-y-2">
      <TimeRangeInput value={value.time} onChange={(time) => onChange({ ...value, time })} legend="実始業・実終業(修正)" />
      <div>
        <label className="block text-xs text-text-sub mb-1">区分</label>
        <Select value={value.reasonCodeId ?? WORKED_OPTION} onValueChange={(v) => onChange({ ...value, reasonCodeId: v === WORKED_OPTION ? null : v })}>
          <SelectTrigger className="w-full min-h-[44px]"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value={WORKED_OPTION}>通常勤務</SelectItem>
            {reasonCodes.map((r) => <SelectItem key={r.reason_code_id} value={r.reason_code_id}>{r.label}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>
      <div>
        <label className="block text-xs text-text-sub mb-1">遅刻・早退の理由(任意)</label>
        <Select value={value.deviationReasonCodeId ?? NONE_OPTION} onValueChange={(v) => onChange({ ...value, deviationReasonCodeId: v === NONE_OPTION ? null : v })}>
          <SelectTrigger className="w-full min-h-[44px]"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value={NONE_OPTION}>なし</SelectItem>
            {deviationReasonCodes.map((r) => <SelectItem key={r.reason_code_id} value={r.reason_code_id}>{r.label}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>
    </div>
  );
}

export type WorkRecordAmendmentPayload = {
  work_record_id: string; row_version: number; start_at: string; end_at: string;
  reason_code_id: string | null; deviation_reason_code_id: string | null;
};

/** draft (ローカルの HH:MM 表現) を API 送信用 (ISO日時) に変換する。時刻未入力なら null (送信不可)。 */
export const buildAmendmentPayload = (
  workRecordId: string, rowVersion: number, workDate: string, draft: WorkRecordAmendDraft,
): WorkRecordAmendmentPayload | null => {
  if (!draft.time.startTime || !draft.time.endTime) return null;
  const endDate = computesCrossesMidnight(draft.time) ? addDays(workDate, 1) : workDate;
  return {
    work_record_id: workRecordId, row_version: rowVersion,
    start_at: buildLocalIso(workDate, draft.time.startTime), end_at: buildLocalIso(endDate, draft.time.endTime),
    reason_code_id: draft.reasonCodeId, deviation_reason_code_id: draft.deviationReasonCodeId,
  };
};
